import { Injectable } from '@angular/core';
import {
	getMortgageLenderNamesForTitleInsurance,
	getMortgageName,
} from '@core/util/mortgage';
import { getFCTPropertyType } from '@core/util/property';
import { checkIfPostalCodeRequired } from '@core/util/title-insurance';
import { isSet } from '@core/util/value';
import {
	ActingForType,
	BusinessMatterClient,
	Matter,
	MatterBusinessUser,
	MatterFileType,
	MatterTitle,
	PersonMatterClient,
	PropertyType,
} from '@models/matter';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import { TitleInsuranceProvider } from '@models/title-insurance';
import { getClientBrokerageFromMatter } from '@state/utils/helper';
import { isEmpty } from 'lodash';
import { UrlService } from 'src/app/shared/services/url/url.service';

export const KEY_DATES_QUERY_PARAM_VALUE = 'keyDates';
export interface MissingFieldSection {
	sectionName: string;
	fields: MissingField[];
}

export interface MissingField {
	openDrawer?: () => void;
	url?: string;
	fieldName: string;
}

@Injectable({
	providedIn: 'root',
})
export class ValidateFctDataService {
	getQuoteMissingSections(
		matter: Matter,
		mortgages: MatterMortgageWithAssociations[],
		matterTitles: MatterTitle[],
	): MissingFieldSection[] {
		const sections: MissingFieldSection[] = [];
		const url = UrlService.Matter_Property_Details(matter.id);

		const propertySection: MissingFieldSection = {
			sectionName: 'Property details',
			fields: [],
		};

		if (!isSet(getFCTPropertyType(matter?.propertyDetails?.propertyType))) {
			propertySection.fields.push({
				fieldName: 'Property type not supported',
				url,
			});
		}

		if (!isSet(matter.propertyDetails.interestEstate)) {
			propertySection.fields.push({
				fieldName: 'Interest/estate',
				url,
			});
		}

		if (!isSet(matter?.propertyDetails?.address?.street)) {
			propertySection.fields.push({
				fieldName: 'Property address - street',
				url,
			});
		}

		if (!isSet(matter?.propertyDetails?.address?.province)) {
			propertySection.fields.push({
				fieldName: 'Property address - province',
				url,
			});
		}

		if (matterTitles.length === 0) {
			if (
				matter?.propertyDetails?.propertyType?.type === PropertyType.CONDO &&
				isEmpty(matterTitles)
			) {
				propertySection.fields.push({
					fieldName: 'PIN',
					url,
				});
			} else if (
				matter?.propertyDetails?.propertyType?.type !== PropertyType.CONDO
			) {
				propertySection.fields.push({
					fieldName: 'PIN',
					url,
				});
			}
		} else {
			matterTitles.forEach((matterTitle) => {
				if (!isSet(matterTitle?.pinNumber)) {
					propertySection.fields.push({
						fieldName: 'PIN',
						url,
					});
				}
			});
		}

		if (matter.fileType === MatterFileType.PURCHASE) {
			if (
				isNaN(matter.purchasePrice) ||
				!isSet(matter.purchasePrice) ||
				matter.purchasePrice === 0
			) {
				propertySection.fields.push({
					fieldName: 'Purchase price',
					url: UrlService.Matter_Adjustments(matter.id),
				});
			}
		}

		if (
			matter?.fileType === MatterFileType.REFINANCE &&
			mortgages?.filter((x) => isSet(x?.priority)).length === 0
		) {
			sections.push({
				sectionName: 'Mortgages',
				fields: [
					{
						fieldName: 'No mortgages on file',
						url: UrlService.Matter_Mortgages(matter.id),
					},
				],
			});
		}

		mortgages
			.filter((x) => isSet(x?.priority))
			.forEach((mortgage) => {
				const mortgageMissingSection: MissingFieldSection = {
					sectionName: getMortgageName(mortgage),
					fields: this.getMortgageMissingFields(matter?.id, mortgage),
				};

				if (mortgageMissingSection?.fields?.length > 0) {
					sections.push(mortgageMissingSection);
				}
			});

		if (propertySection.fields.length > 0) {
			sections.push(propertySection);
		}

		return sections;
	}

	getSubmitDataMissingSections(
		matter: Matter,
		clients: (PersonMatterClient | BusinessMatterClient)[],
		mortgages: MatterMortgageWithAssociations[],
		matterTitles: MatterTitle[],
	): MissingFieldSection[] {
		const sections: MissingFieldSection[] = [];

		const lawyerSection: MissingFieldSection = {
			sectionName: 'Lawyer',
			fields: this.getLawyerMissingFields(matter.id, matter.lawyerAssigned),
		};

		if (lawyerSection.fields.length > 0) {
			sections.push(lawyerSection);
		}

		const transactionSection: MissingFieldSection = {
			sectionName: 'Transaction details',
			fields: this.getTransactionMissingFields(matter),
		};

		if (transactionSection.fields.length > 0) {
			sections.push(transactionSection);
		}

		const clientSection: MissingFieldSection = {
			sectionName: `${matter.fileType === MatterFileType.PURCHASE ? 'Purchaser' : 'Mortgagor'} details`,
			fields: [],
		};

		if (clients.length === 0) {
			if (matter.fileType === MatterFileType.PURCHASE) {
				clientSection.fields.push({
					fieldName: 'No purchaser on file',
					url: UrlService.Matter_Parties(matter.id),
				});
			}
			if (matter.fileType === MatterFileType.REFINANCE) {
				clientSection.fields.push({
					fieldName: 'No mortgagor on file',
					url: UrlService.Matter_Parties_Mortgagors(matter.id),
				});
			}
		}

		if (clientSection.fields.length > 0) {
			sections.push(clientSection);
		}

		const propertySection: MissingFieldSection = {
			sectionName: 'Property details',
			fields: this.getPropertyMissingFields(matter, matterTitles) || [],
		};

		if (propertySection.fields.length > 0) {
			sections.push(propertySection);
		}

		if (
			matter.fileType === MatterFileType.REFINANCE &&
			matter.actingFor === ActingForType.MORTGAGEE
		) {
			const borrowersSolicitorSection: MissingFieldSection = {
				sectionName: 'Borrowers Solicitor',
				fields: this.getBorrowersSolicitorMissingFields(matter),
			};

			if (borrowersSolicitorSection.fields.length > 0) {
				sections.push(borrowersSolicitorSection);
			}
		}

		if (
			matter?.fileType === MatterFileType.REFINANCE &&
			mortgages?.filter((x) => isSet(x?.priority)).length === 0
		) {
			sections.push({
				sectionName: 'Mortgages',
				fields: [
					{
						fieldName: 'No mortgages on file',
						url: UrlService.Matter_Mortgages(matter.id),
					},
				],
			});
		}

		mortgages
			.filter((x) => isSet(x?.priority))
			.forEach((mortgage) => {
				const mortgageMissingSection: MissingFieldSection = {
					sectionName: getMortgageName(mortgage),
					fields: this.getMortgageMissingFields(matter?.id, mortgage),
				};

				if (mortgageMissingSection?.fields?.length > 0) {
					sections.push(mortgageMissingSection);
				}
			});

		return sections;
	}

	private getLawyerMissingFields(
		matterId: string,
		lawyer: MatterBusinessUser,
	): MissingField[] {
		const result: MissingField[] = [];

		if (!lawyer) {
			result.push({
				fieldName: 'No lawyer assigned',
				url: UrlService.Matter(matterId),
			});
		}

		return result;
	}

	private getTransactionMissingFields(matter: Matter): MissingField[] {
		const fields: MissingField[] = [];

		if (!isSet(matter.closingDate)) {
			fields.push({
				fieldName: 'Closing date',
				url: UrlService.Matter_TitleInsurance(matter.id),
			});
		}

		if (matter.fileType === MatterFileType.PURCHASE) {
			if (
				!(
					isSet(matter.otherPartyLawFirm) &&
					isSet(matter.otherPartyLawFirm?.lawyer)
				)
			) {
				fields.push({
					fieldName: 'Vendor lawyer',
					url: UrlService.Matter_Parties_OtherParties(matter.id),
				});
			}
		}

		const clientBrokerage = getClientBrokerageFromMatter(matter);

		if (clientBrokerage) {
			if (isSet(clientBrokerage.broker)) {
				if (!isSet(clientBrokerage.name)) {
					fields.push({
						fieldName: 'Purchaser brokerage name',
						url: UrlService.Matter_Parties(matter.id),
					});
				}
				if (!isSet(clientBrokerage.broker?.firstName)) {
					fields.push({
						fieldName: 'Purchaser broker first name',
						url: UrlService.Matter_Parties(matter.id),
					});
				}
				if (!isSet(clientBrokerage.broker?.lastName)) {
					fields.push({
						fieldName: 'Purchaser broker last name',
						url: UrlService.Matter_Parties(matter.id),
					});
				}
				if (
					!(
						isSet(clientBrokerage.phoneNumber) ||
						isSet(clientBrokerage.broker?.phoneNumber) ||
						isSet(clientBrokerage.broker?.workPhone)
					)
				) {
					fields.push({
						fieldName: 'Purchaser broker phone number',
						url: UrlService.Matter_Parties(matter.id),
					});
				}
			}
		}

		return fields;
	}

	private getMortgageMissingFields(
		matterId: string,
		mortgage: MatterMortgageWithAssociations,
	): MissingField[] {
		const fields: MissingField[] = [];
		const url = UrlService.Matter_Mortgages_Mortgage(matterId, mortgage.id);
		const mortgageLenderName =
			getMortgageLenderNamesForTitleInsurance(mortgage);

		if (!isSet(mortgageLenderName)) {
			fields.push({
				fieldName: 'Lender name',
				url,
			});
		} else if (mortgageLenderName.length > 100) {
			fields.push({
				fieldName: 'Lender name must be 100 characters or fewer',
				url,
			});
		}

		if (!isSet(mortgage?.amount)) {
			fields.push({
				fieldName: 'Amount',
				url,
			});
		} else if (mortgage?.amount === 0) {
			fields.push({
				fieldName: "Amount can't be $0.00",
				url,
			});
		}

		if (mortgage?.mms && !isSet(mortgage.dealId)) {
			fields.push({
				fieldName: 'Deal ID',
				url,
			});
		}

		if (
			mortgage?.mms &&
			isSet(mortgage?.dealId) &&
			mortgage?.dealId?.replace(/ /, '')?.length !== 11
		) {
			fields.push({
				fieldName: 'Deal ID must be 11 characters',
				url,
			});
		}

		if (
			isSet(mortgage.referenceNumber) &&
			mortgage.referenceNumber.length > 50
		) {
			fields.push({
				fieldName: 'Reference number must be 50 characters or fewer',
				url,
			});
		}

		return fields;
	}

	private getBorrowersSolicitorMissingFields(matter: Matter): MissingField[] {
		const fields: MissingField[] = [];
		const url =
			matter.fileType === MatterFileType.PURCHASE
				? UrlService.Matter_Parties_OtherParties(matter.id)
				: UrlService.Matter_Parties_Mortgagors(matter.id);

		if (!isSet(matter?.otherPartyLawFirm?.name)) {
			fields.push({
				fieldName: 'Law firm name',
				url,
			});
		}

		if (!isSet(matter?.otherPartyLawFirm?.lawyer?.firstName)) {
			fields.push({
				fieldName: 'Lawyer first name',
				url,
			});
		}

		if (!isSet(matter?.otherPartyLawFirm?.lawyer?.firstName)) {
			fields.push({
				fieldName: 'Lawyer last name',
				url,
			});
		}

		if (
			!(
				isSet(matter?.otherPartyLawFirm?.lawyer?.phoneNumber) ||
				isSet(matter?.otherPartyLawFirm?.phoneNumber)
			)
		) {
			fields.push({
				fieldName: 'Lawyer phone number',
				url,
			});
		}

		return fields;
	}

	private getPropertyMissingFields(
		matter: Matter,
		matterTitles: MatterTitle[],
	): MissingField[] {
		const fields: MissingField[] = [];
		const url = UrlService.Matter_Property_Details(matter.id);

		if (matter.fileType === MatterFileType.PURCHASE) {
			if (!isSet(matter.purchasePrice) || matter.purchasePrice === 0) {
				fields.push({
					fieldName: 'Purchase price',
					url: UrlService.Matter_Adjustments(matter.id),
				});
			}
		}

		if (!isSet(matter.propertyDetails.interestEstate)) {
			fields.push({
				fieldName: 'Property address - Interest/estate',
				url,
			});
		}

		if (!isSet(matter?.propertyDetails?.address?.street)) {
			fields.push({
				fieldName: 'Property address - street',
				url,
			});
		}

		if (!isSet(matter?.propertyDetails?.address?.city)) {
			fields.push({
				fieldName: 'Property address - city',
				url,
			});
		}

		if (!isSet(matter?.propertyDetails?.address?.province)) {
			fields.push({
				fieldName: 'Property address - province',
				url,
			});
		}

		if (
			!isSet(matter?.propertyDetails?.address?.postcode) &&
			checkIfPostalCodeRequired(
				TitleInsuranceProvider.FCT,
				matter?.propertyDetails,
			)
		) {
			fields.push({
				fieldName: 'Property address - postal code',
				url,
			});
		}

		if (matterTitles.length === 0) {
			if (
				matter?.propertyDetails?.propertyType?.type === PropertyType.CONDO &&
				isEmpty(matterTitles)
			) {
				fields.push({
					fieldName: 'PIN',
					url,
				});
			} else if (
				matter?.propertyDetails?.propertyType?.type !== PropertyType.CONDO
			) {
				fields.push({
					fieldName: 'PIN',
					url,
				});
			}
		} else {
			matterTitles.forEach((matterTitle) => {
				if (!isSet(matterTitle?.pinNumber)) {
					fields.push({
						fieldName: 'PIN',
						url,
					});
				}
			});
		}

		return fields;
	}
}
